/**
 * AaO Size Guard — Swatch King edition (v2, cross-page)
 * -----------------------------------------------------
 * When a customer switches colors and their intended size is no longer offered
 * in that color, Swatch King silently removes that size from the picker and
 * auto-selects a different one. This script detects that and:
 *
 *  1. Shows a message: "Size XL isn't available in this color. Please select a size."
 *  2. Blocks add-to-cart until the customer explicitly picks a size.
 *  3. Once the customer clicks any size, the block clears and checkout proceeds.
 *
 * v2 additions (2026-07): the chosen size now persists across page navigation
 * via sessionStorage. When the customer clicks through to a sibling product
 * (t-shirt → hoodie), their size is auto-applied if available; if it isn't,
 * add-to-cart is blocked with a message until they explicitly pick a size.
 * Cross-page restore only runs on /products/ pages.
 *
 * NOTE: We do NOT wipe the variant ID. Swatch King manages that. We only
 * overlay a submit block via a flag, cleared when the customer makes an
 * explicit size choice.
 */
(function () {
  'use strict';

  // ── State ──────────────────────────────────────────────────────────────────
  let intendedSize  = null;
  let applyPending  = false;
  let guardBlocked  = false; // true when intended size is missing from current color
  let crossPage     = false; // intent restored from a previous page, not yet re-clicked here

  // ── Constants ──────────────────────────────────────────────────────────────
  const BUTTONS_WRAP_SEL = '.product-form__buttons';
  const MSG_ID           = 'aao-size-msg';
  const STORE_KEY        = 'aao-intended-size';
  const IS_PRODUCT_PAGE  = /^\/products\//.test(location.pathname);

  // sessionStorage can throw (e.g. blocked storage); the guard must never break the page
  function readStoredSize() {
    try { return sessionStorage.getItem(STORE_KEY); } catch (e) { return null; }
  }
  function writeStoredSize(v) {
    try { sessionStorage.setItem(STORE_KEY, v); } catch (e) { /* non-fatal */ }
  }

  // ── Swatch King helpers ────────────────────────────────────────────────────

  function getSizeGroup() {
    const groups = document.querySelectorAll('ul[role="radiogroup"]');
    for (const g of groups) {
      const labelId = g.getAttribute('aria-labelledby');
      if (!labelId) continue;
      const label = document.getElementById(labelId);
      if (label && /size/i.test(label.textContent)) return g;
    }
    return null;
  }

  function getSizeItems(group) {
    if (!group) return [];
    return [...group.querySelectorAll('li.swatch-view-item[role="radio"]')];
  }

  function getItemValue(item) {
    return item.getAttribute('orig-value') ||
           item.querySelector('[data-value]')?.dataset.value ||
           item.textContent.trim();
  }

  function unavailableMessage() {
    return crossPage
      ? `Size ${intendedSize} isn’t available for this item. Please select a size before adding to cart.`
      : `Size ${intendedSize} isn’t available in this color. Please select a size.`;
  }

  // ── 1. Track explicit size selection ──────────────────────────────────────
  document.addEventListener('click', (e) => {
    const item = e.target.closest('li.swatch-view-item[role="radio"]');
    if (!item) return;

    const group = item.closest('ul[role="radiogroup"]');
    if (!group) return;
    const labelId = group.getAttribute('aria-labelledby');
    if (!labelId) return;
    const label = document.getElementById(labelId);
    if (!label || !/size/i.test(label.textContent)) return;

    // Customer explicitly chose a size — update intent, lift any block
    intendedSize = getItemValue(item);
    writeStoredSize(intendedSize);
    crossPage    = false;
    guardBlocked = false;
    clearMessage();
  }, true);

  // ── 2. Watch for color-switch DOM changes ─────────────────────────────────
  function onDomChange() {
    if (!intendedSize || applyPending) return;
    applyPending = true;
    requestAnimationFrame(() => {
      checkIntendedSize();
      applyPending = false;
    });
  }

  const observer = new MutationObserver(onDomChange);

  function attachObserver() {
    const root = document.querySelector('product-info') || document.querySelector('main');
    if (root) observer.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachObserver);
  } else {
    attachObserver();
  }

  // ── 3. After color switch, check if intended size still exists ────────────
  function checkIntendedSize() {
    const group = getSizeGroup();
    const items = getSizeItems(group);
    if (!items.length) return; // no size option on this product (or not rendered yet)

    const target = items.find((i) => getItemValue(i) === intendedSize);

    // Disconnect before any DOM changes so we don't re-trigger ourselves
    observer.disconnect();

    if (!target) {
      // Size gone from this color — block cart and warn
      guardBlocked = true;
      showMessage(unavailableMessage());
    } else {
      // Size exists in this color — lift block and clear warning
      guardBlocked = false;
      clearMessage();
      // Re-click if Swatch King auto-selected something else while we were away
      const alreadySelected = target.getAttribute('aria-checked') === 'true' ||
                              !!target.querySelector('.swatch-selected');
      if (!alreadySelected) {
        target.click(); // lets Swatch King update variant ID and URL correctly
      }
      crossPage = false; // intent is now applied on this page
    }

    attachObserver();
  }

  // ── 3b. Cross-page restore: pick up where the customer left off ────────────
  // Swatch King renders after us, so poll until the size group appears; the
  // mutation observer keeps re-checking after that (progressive render, color
  // switches). Product pages only — collection cards also use SK radiogroups.
  if (IS_PRODUCT_PAGE) {
    const stored = readStoredSize();
    if (stored) {
      intendedSize = stored;
      crossPage    = true;
      let tries = 0;
      const poll = setInterval(() => {
        tries += 1;
        if (getSizeItems(getSizeGroup()).length) {
          clearInterval(poll);
          checkIntendedSize();
        } else if (tries >= 40) { // ~12s — SK never rendered a size group
          clearInterval(poll);
          if (document.querySelector('product-form')) {
            // Selector contract with Swatch King may have changed — flag it
            console.warn('AaO size guard: size swatches not found on product page');
          }
        }
      }, 300);
    }
  }

  // ── 4. Block add-to-cart while guard is active ────────────────────────────
  document.addEventListener(
    'submit',
    (e) => {
      if (!e.target.closest('product-form')) return;
      if (!guardBlocked) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      showMessage(unavailableMessage());
    },
    true
  );

  // ── Helpers ────────────────────────────────────────────────────────────────

  // Grey out add-to-cart (and Buy It Now, which bypasses form submit) while
  // blocked. Dawn also disables these buttons for sold-out variants, so mark
  // the ones WE disabled and only ever re-enable those.
  let btnDisabledByGuard = false;
  function setAtcDisabled(on) {
    if (on) {
      const btns = document.querySelectorAll(
        `${BUTTONS_WRAP_SEL} button[name="add"], ${BUTTONS_WRAP_SEL} .shopify-payment-button button`
      );
      btns.forEach((b) => {
        if (!b.disabled) {
          b.disabled = true;
          b.setAttribute('data-aao-disabled', '');
          b.setAttribute('aria-disabled', 'true');
        }
      });
      btnDisabledByGuard = true;
    } else if (btnDisabledByGuard) {
      document.querySelectorAll('[data-aao-disabled]').forEach((b) => {
        b.disabled = false;
        b.removeAttribute('data-aao-disabled');
        b.removeAttribute('aria-disabled');
      });
      btnDisabledByGuard = false;
    }
  }

  function showMessage(text) {
    let msg = document.getElementById(MSG_ID);
    if (!msg) {
      msg = document.createElement('p');
      msg.id = MSG_ID;
      msg.setAttribute('role', 'alert');
      msg.style.cssText = [
        'margin: 0 0 1rem 0',
        'font-size: 1.3rem',
        'color: rgb(var(--color-error, 176, 0, 32))',
        'font-weight: 500',
      ].join(';');
      const wrap = document.querySelector(BUTTONS_WRAP_SEL);
      if (wrap) wrap.prepend(msg);
    }
    msg.textContent = text;
    setAtcDisabled(true);
  }

  function clearMessage() {
    const msg = document.getElementById(MSG_ID);
    if (msg) msg.remove();
    setAtcDisabled(false);
  }
})();
